import Head from 'expo-router/head';
import type { ReactNode } from 'react';

import { useLocale } from '@todo/core/i18n/use-locale';
import { absoluteUrl, hreflangLinks, type HreflangLink } from '@todo/core/seo';

type Props = {
  title: string;
  description: string;
  /** Route path under the locale, e.g. 'tours' ('' = home). */
  path: string;
  image?: string | null;
  /**
   * Opt the page into search indexes. Everything else ships `noindex, follow`:
   * the site is indexed page type by page type as each is ready (home and live
   * tour pages, /tours and the collections with enough tours so far), and the sitemap lists only pages without noindex.
   */
  indexable?: boolean;
  /**
   * The page's hreflang set when its URL isn't the same path in every
   * language (a guide: each version has its own slug). Default: `path` under
   * every locale.
   */
  alternates?: HreflangLink[];
  /** og:type — 'article' for a guide. */
  ogType?: 'website' | 'article';
  children?: ReactNode;
};

// The per-page <head>: title, description, canonical, hreflang alternates and
// the Open Graph / Twitter tags. Extra head elements (JSON-LD, list seeds) go
// in as children — literal elements only (react-helmet-async's rule).
export function PageHead({ title, description, path, image, indexable, alternates, ogType = 'website', children }: Props) {
  const { locale } = useLocale();
  const url = absoluteUrl(locale, path);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {(alternates ?? hreflangLinks(path)).map((l) => (
        <link key={l.hrefLang} rel="alternate" hrefLang={l.hrefLang} href={l.href} />
      ))}
      {indexable ? null : <meta name="robots" content="noindex, follow" />}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      {image ? <meta property="og:image" content={image} /> : null}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {children}
    </Head>
  );
}
